// The Branches page: a table of branches (with their parent hub) and CRUD.
//
// Loads both hubs (for the parent dropdown + showing the hub name) and branches
// for the active account.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Spinner } from "../../components/ui";
import { fmtN } from "../../lib/format";
import { getErrorMessage } from "../../lib/errors";
import type { Branch, Hub } from "../../lib/types";
import { listHubs } from "../hubs/hubsApi";
import { listBranches, setBranchActive } from "./branchesApi";
import BranchForm from "./BranchForm";

export default function BranchList() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [hubs, setHubs] = useState<Hub[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // undefined = closed, null = creating, Branch = editing.
  const [formFor, setFormFor] = useState<Branch | null | undefined>(undefined);
  const [showInactive, setShowInactive] = useState(true);

  // Quick lookup of hub name by id, for the table.
  const hubName = useMemo(() => {
    const m = new Map(hubs.map((h) => [h.id, h.name]));
    return (id: string) => m.get(id) ?? "—";
  }, [hubs]);

  const reload = useCallback(async () => {
    if (!accountId) {
      // No active account yet — don't get stuck on the loading state.
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [h, b] = await Promise.all([listHubs(accountId), listBranches(accountId)]);
      setHubs(h);
      setBranches(b);
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to load branches."));
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function toggleActive(branch: Branch) {
    if (!accountId) return;
    try {
      await setBranchActive(accountId, branch.id, !branch.active);
      await reload();
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to update branch."));
    }
  }

  const visibleBranches = showInactive ? branches : branches.filter((b) => b.active);

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Branches</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <Button onClick={() => setFormFor(null)}>+ New branch</Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && hubs.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          You need at least one hub before adding branches. Create one on the Hubs page.
        </div>
      )}

      <Card className="p-0">
        {loading ? (
          <Spinner />
        ) : branches.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">
            No branches yet. Click <span className="font-medium">New branch</span> to add your first one.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3 font-medium">Branch</th>
                <th className="px-5 py-3 font-medium">Code</th>
                <th className="px-5 py-3 font-medium">Parent hub</th>
                <th className="px-5 py-3 font-medium">Location</th>
                <th className="px-5 py-3 font-medium">Capacity</th>
                <th className="px-5 py-3 font-medium">Manager</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {visibleBranches.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-3 font-medium text-slate-900">{b.name}</td>
                  <td className="px-5 py-3 text-slate-600">{b.code ?? "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{hubName(b.hubId)}</td>
                  <td className="px-5 py-3 text-slate-600">
                    {[b.city, b.state].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {b.capacity != null ? fmtN(b.capacity) : "—"}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{b.manager ?? "—"}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        b.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {b.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setFormFor(b)}>
                        Edit
                      </Button>
                      <Button variant="ghost" onClick={() => toggleActive(b)}>
                        {b.active ? "Pause" : "Activate"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {formFor !== undefined && (
        <BranchForm
          key={formFor?.id ?? "new"}
          accountId={accountId}
          branch={formFor}
          hubs={hubs}
          existingBranches={branches}
          onClose={() => setFormFor(undefined)}
          onSaved={() => {
            setFormFor(undefined);
            reload();
          }}
        />
      )}
    </div>
  );
}
